import React, { useState } from 'react';
import { Table, Loader, Select, Grid, GridCol, Title } from '@mantine/core';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { EquipmentList } from '../../../../libraries/helpdesk/contracts';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

export enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

interface ContractEquipmentTableProps {
  title: string;
  contractEquipments: EquipmentList[];
  optionsEquipments: EquipmentList[];
  onRemoveEquipment: (eid: number) => void;
  loading: boolean;
  editable: boolean;
  form: any;
}

const ContractEquipmentTable: React.FC<ContractEquipmentTableProps> = ({
  title,
  contractEquipments,
  optionsEquipments,
  onRemoveEquipment,
  loading,
  editable,
  form
}) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currentEquipments = form.values.equipmentList as EquipmentList[];
  const [selectValue, setSelectValue] = useState<string | null>(null);
  const availableOptions = optionsEquipments.filter(opt => !contractEquipments.some(eq => eq.eid === opt.eid));
  const selectData = availableOptions.map(eq => ({
    value: eq.eid.toString(),
    label: `${eq.label}`
  }));

  const handleSelectChange = (value: string | null) => {
    if (!value) return;
    const selected = availableOptions.find(eq => eq.eid.toString() === value);
    if (!selected) return;

    const updatedList = [...currentEquipments, selected];
    form.setFieldValue('equipmentList', updatedList);
    setSelectValue(null);
  };

  const handleRemove = (eid: number) => {
    const updatedList = currentEquipments.filter(eq => eq.eid !== eid);
    form.setFieldValue('equipmentList', updatedList);
    onRemoveEquipment(eid);
  };

  return (
    <Grid className="contract-equipment-table">
      <Title order={2}>{title}</Title>
      <GridCol span={12}>
        <Select data={selectData} value={selectValue} onChange={handleSelectChange} disabled={!editable} />

        <Table.ScrollContainer minWidth={400}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>EID</Table.Th>
                <Table.Th>SN</Table.Th>
                {editable && <Table.Th />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : contractEquipments.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={3} style={{ textAlign: 'center' }}>
                    {lang.GLOBAL_ALL}
                  </Table.Td>
                </Table.Tr>
              ) : (
                contractEquipments.map(eq => (
                  <Table.Tr key={eq.eid}>
                    <Table.Td>{eq.eid}</Table.Td>
                    <Table.Td>{eq.sn}</Table.Td>
                    {editable && (
                      <Table.Td className="x">
                        <Icon.IconX stroke={2} onClick={() => handleRemove(eq.eid)} style={{ cursor: 'pointer' }} />
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </GridCol>
    </Grid>
  );
};

export default ContractEquipmentTable;
